import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {UAParser} from 'ua-parser-js';

@Injectable({
    providedIn: 'root',
})
export class DeviceDetectionService {
    private readonly currentDevice: Device;
    private screenHeight = 0;
    private screenWidth = 0;

    constructor() {
        const parser = new UAParser();
        parser.setUA(navigator.userAgent);
        const result = parser.getResult();

        /*manage screen orientation and size*/
        result.device.dpr = window.devicePixelRatio;
        result.device.width = window.innerWidth;
        result.device.height = window.innerHeight;
        result.device.orientation = ScreenOrientation.PORTRAIT;
        result.device.userAgent = navigator.userAgent;

        if (this.screenHeight < this.screenWidth) {
            result.device.height = window.innerWidth;
            result.device.width = window.innerHeight;
            result.device.orientation = ScreenOrientation.LANDSCAPE;
        }

        this.currentDevice = new Device(result.ua, result.browser, result.device, result.os, result.engine, result.cpu);
    }


    getDevice(): Device {
        return this.currentDevice;
    }

}

export class Device {
    public ua: string;
    public browser: DeviceBrowser;
    public device: DeviceData;
    public os: DeviceOS;
    public engine: DeviceEngine;
    public cpu: DeviceCPU;

    constructor(ua: string, browser: DeviceBrowser, deviceData: DeviceData, deviceOS: DeviceOS, deviceEngine: DeviceEngine, deviceCPU: DeviceCPU) {
        this.ua = ua;
        this.browser = browser;
        this.device = deviceData;
        this.os = deviceOS;
        this.engine = deviceEngine;
        this.cpu = deviceCPU;

        this.initOrientationChange();
        this.initResizeEvent();

        this.setOrientation(deviceData.orientation);
    }

    private _orientationChanged: BehaviorSubject<any> = new BehaviorSubject(ScreenOrientation.PORTRAIT);
    get orientationChanged(): Observable<ScreenOrientation> {
        return this._orientationChanged.pipe();
    }

    private _screenSizeChanged: Subject<{ height: number, width: number }> = new Subject();
    get screenSizeChanged(): Observable<{ height: number, width: number }> {
        return this._screenSizeChanged.pipe();
    }

    public setOrientation(screenOrientation: ScreenOrientation) {
        if (this.device.orientation !== screenOrientation) {
            this._orientationChanged.next(screenOrientation);
            this.device.orientation = screenOrientation;
        }
    }

    public getOrientation() {
        return this.device.orientation;
    }

    private initResizeEvent() {
        window.addEventListener('resize', (event) => {
            let h = window.innerHeight;
            let w = window.innerWidth;

            if (this.device.orientation == ScreenOrientation.LANDSCAPE) {
                w = window.innerHeight;
                h = window.innerWidth;
            }

            this.device.width = w;
            this.device.height = h;
            this._screenSizeChanged.next({height: h, width: w});
        });
    }

    private initOrientationChange() {
        screen.orientation.addEventListener('change', (event: any) => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            event.preventDefault();
            if (event.target.type.match(/landscape/)) {
                this.setOrientation(ScreenOrientation.LANDSCAPE);
            } else {
                this.setOrientation(ScreenOrientation.PORTRAIT);
            }
        });
    }


}

export interface DeviceCPU {
    a: any;
}

export interface DeviceEngine {
    name: string;
    version: string;
}

export interface DeviceOS {
    name: string,
    version: string
}

export interface DeviceData {
    model: string;
    type: string;
    vendor: string;
    width: number;
    height: number;
    dpr: number;
    orientation: ScreenOrientation;
    userAgent: string;
}

export interface DeviceBrowser {
    name: string;
    version: string;
    major: string;
}

export enum ScreenOrientation {
    LANDSCAPE = 'landscape',
    PORTRAIT = 'portrait'
}

